/**
 * Protected Admin Route Component
 *
 * Guards admin routes to ensure only platform administrators can access them.
 * Redirects non-platform-admin users to the dashboard.
 *
 * Feature: BUG-LOCAL-001 - Admin Dashboard Access Control
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, isPlatformAdmin } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to dashboard if authenticated but not a platform admin
  if (!isPlatformAdmin) {
    // Log unauthorized access attempt for security monitoring
    console.warn('[SECURITY] Non-platform-admin attempted to access admin route');
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and is a platform admin - render children
  return <>{children}</>;
};
